'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { matchDistrict, matchState } from '@/lib/geo/match';
import { reverseGeocode } from '@/lib/geo/google-geocode';
import { wclApiService } from '@/services/wcl-api.service';
import { BROWSER_GEO_BLOCKED_MESSAGE } from '@/lib/geo/browser-permission';
import {
  type GeoCacheEntry,
  type PermissionState,
  GEO_UPDATED_EVENT,
  readGeoCache,
  readPermissionState,
  writeGeoCache,
  writePermissionState,
} from '@/lib/geo/storage';

interface NamedItem {
  id: string;
  name: string;
}

/** Promisified `getCurrentPosition` — must be called synchronously from the
 *  user-gesture turn (see caller note) so it keeps user activation. */
function requestCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10_000,
      maximumAge: 5 * 60 * 1000,
    });
  });
}

export type GeoStatus =
  | 'idle'
  | 'loading-cache'
  | 'cache-hit'
  | 'requesting-permission'
  | 'permission-denied'
  | 'unsupported'
  | 'geocoding'
  | 'matching'
  | 'matched'
  | 'partial-match'
  | 'no-match'
  | 'error';

export interface GeoDetectResult {
  status: GeoStatus;
  /** Matched WCL state ID, ready to feed into selectedState. */
  stateId: string | null;
  stateName: string | null;
  /** Matched WCL district ID, ready to feed into selectedDistrict. */
  districtId: string | null;
  districtName: string | null;
  /** 6-digit Indian pincode from reverse geocode, when available. */
  pincode: string | null;
  /** Browser geolocation coords — used to center the map on first detect. */
  lat: number | null;
  lng: number | null;
  /** Raw Google response names — surfaced for diagnostics + the user-facing
   *  banner ("Showing dealers near Mumbai, Maharashtra"). */
  rawStateName: string | null;
  rawDistrictName: string | null;
  /** True while the hook is doing any async work. */
  isWorking: boolean;
  /** Cause of error/no-match — for the user-facing message. */
  errorMessage: string | null;
}

interface UseGeoDetectOptions {
  /** When false, the hook stays in `idle` and never tries auto-detect.
   *  Set false if the parent already has explicit state/district selected
   *  (e.g., URL params) — we don't want to overwrite that. */
  enabled?: boolean;
  /** If true, the hook auto-fires geolocation on mount (when no cache).
   *  If false, the hook waits for `triggerDetect()` to be called explicitly
   *  (from a "Use my location" button click). Default: true. */
  autoPrompt?: boolean;
}

type ReverseResult = {
  stateName: string | null;
  districtName: string | null;
  city?: string | null;
  pincode: string | null;
};

/** Build a fully-populated `cache-hit` result from a stored cache entry. */
function cacheHitResult(cached: GeoCacheEntry): GeoDetectResult {
  return {
    status: 'cache-hit',
    stateId: cached.stateId,
    stateName: cached.stateName,
    districtId: cached.districtId,
    districtName: cached.districtName,
    pincode: cached.pincode ?? null,
    lat: cached.lat ?? null,
    lng: cached.lng ?? null,
    rawStateName: cached.rawStateName,
    rawDistrictName: cached.rawDistrictName,
    isWorking: false,
    errorMessage: null,
  };
}

/** Resolve the district from the reverse-geocode result, preferring the
 *  district name and falling back to the city when they differ. */
function resolveDistrictMatch(reverse: ReverseResult, districts: NamedItem[]): NamedItem | null {
  const matchedDistrict = reverse.districtName
    ? matchDistrict(reverse.districtName, districts)
    : null;
  const matchedDistrictFromCity =
    !matchedDistrict && reverse.city && reverse.city !== reverse.districtName
      ? matchDistrict(reverse.city, districts)
      : null;
  return matchedDistrict ?? matchedDistrictFromCity;
}

/** Cache entry for a geocode that matched no WCL state (keeps raw names + pincode). */
function emptyMatchCacheEntry(lat: number, lng: number, reverse: ReverseResult) {
  return {
    lat,
    lng,
    stateId: null,
    stateName: null,
    districtId: null,
    districtName: null,
    pincode: reverse.pincode,
    rawStateName: reverse.stateName,
    rawDistrictName: reverse.districtName,
  };
}

/** Emit a `partial-match` result (state detected, district unresolved) and
 *  persist the state-only entry to the geo cache. */
function applyPartialStateMatch(
  apply: (next: Partial<GeoDetectResult>) => void,
  args: {
    matchedState: NamedItem;
    reverse: ReverseResult;
    lat: number;
    lng: number;
    errorMessage: string;
    rawDistrictName: string | null;
  },
): void {
  const { matchedState, reverse, lat, lng, errorMessage, rawDistrictName } = args;
  apply({
    status: 'partial-match',
    isWorking: false,
    stateId: matchedState.id,
    stateName: matchedState.name,
    districtId: null,
    districtName: null,
    pincode: reverse.pincode,
    lat,
    lng,
    errorMessage,
  });
  writeGeoCache({
    lat,
    lng,
    stateId: matchedState.id,
    stateName: matchedState.name,
    districtId: null,
    districtName: null,
    pincode: reverse.pincode,
    rawStateName: reverse.stateName,
    rawDistrictName,
  });
}

/** Emit a full `matched` result (state + district resolved) and persist it. */
function applyFullMatch(
  apply: (next: Partial<GeoDetectResult>) => void,
  args: {
    matchedState: NamedItem;
    resolvedDistrict: NamedItem;
    reverse: ReverseResult;
    lat: number;
    lng: number;
  },
): void {
  const { matchedState, resolvedDistrict, reverse, lat, lng } = args;
  apply({
    status: 'matched',
    isWorking: false,
    stateId: matchedState.id,
    stateName: matchedState.name,
    districtId: resolvedDistrict.id,
    districtName: resolvedDistrict.name,
    pincode: reverse.pincode,
    lat,
    lng,
    errorMessage: null,
  });
  writeGeoCache({
    lat,
    lng,
    stateId: matchedState.id,
    stateName: matchedState.name,
    districtId: resolvedDistrict.id,
    districtName: resolvedDistrict.name,
    pincode: reverse.pincode,
    rawStateName: reverse.stateName,
    rawDistrictName: reverse.districtName ?? reverse.city ?? null,
  });
}

/** Handle a reverse-geocode that yielded neither state nor district: cache any
 *  pincode we got and emit `no-match`. */
function applyNoGeoMatch(
  apply: (next: Partial<GeoDetectResult>) => void,
  reverse: ReverseResult,
  lat: number,
  lng: number,
): void {
  // Keep pincode in cache when we at least got a postal code.
  if (reverse.pincode) {
    writeGeoCache({
      lat,
      lng,
      stateId: null,
      stateName: null,
      districtId: null,
      districtName: null,
      pincode: reverse.pincode,
      rawStateName: null,
      rawDistrictName: null,
    });
  }
  apply({
    status: 'no-match',
    isWorking: false,
    errorMessage: "Couldn't determine your region. Please select manually.",
    rawStateName: null,
    rawDistrictName: null,
    pincode: reverse.pincode,
    lat,
    lng,
  });
}

/** Merge a cache entry written by another hook instance into the current result,
 *  keeping in-flight work and not downgrading an already-resolved district. */
function reconcileAdoptedCache(prev: GeoDetectResult, cached: GeoCacheEntry): GeoDetectResult {
  if (prev.isWorking) return prev;
  if (prev.status === 'matched' || prev.status === 'cache-hit') {
    // Refresh fields if the cache gained a district later.
    if (prev.districtId || !cached.districtId) return prev;
  }
  return cacheHitResult(cached);
}

const INITIAL: GeoDetectResult = {
  status: 'idle',
  stateId: null,
  stateName: null,
  districtId: null,
  districtName: null,
  pincode: null,
  lat: null,
  lng: null,
  rawStateName: null,
  rawDistrictName: null,
  isWorking: false,
  errorMessage: null,
};

/**
 * Geo detection orchestrator for the dealer-locator page.
 *
 * Flow:
 *   1. On mount (if enabled+autoPrompt), check localStorage cache. If
 *      we have a < 24h-old detection, surface it as `cache-hit` — caller
 *      pre-fills the dropdowns and fires the search synchronously.
 *   2. If no cache and the user has previously denied/dismissed in this
 *      session (sessionStorage), stay in `permission-denied` so the
 *      caller falls back to manual UI without re-prompting.
 *   3. Otherwise, request browser geolocation. Browser shows its native
 *      permission prompt. If granted → step 4. If denied → cache the
 *      decline for the session and surface `permission-denied`.
 *   4. With lat/lng in hand, call Google's Geocoding API for the address.
 *   5. Fuzzy-match Google's state + district names against the WCL state
 *      and district lists (which we fetch internally — caller doesn't
 *      need to plumb them in).
 *   6. Cache the result (lat/lng + matched IDs + names) and surface
 *      `matched` (or `partial-match` if state hit but district didn't,
 *      or `no-match` if neither). Caller decides UX from there.
 *
 * `triggerDetect()` is exposed so the "Use my location" button can
 * re-run the flow on demand even after a denial.
 */
export function useGeoDetect(options: UseGeoDetectOptions = {}): GeoDetectResult & {
  triggerDetect: () => void;
} {
  const { enabled = true, autoPrompt = true } = options;
  const [result, setResult] = useState<GeoDetectResult>(INITIAL);
  // Latest-call guard: if the user clicks "Use my location" twice rapidly,
  // we don't want a stale earlier resolution to overwrite the newer one.
  const runIdRef = useRef(0);
  const hasAutoRunRef = useRef(false);

  const runDetect = useCallback(async () => {
    const myRunId = ++runIdRef.current;
    const isLatest = () => runIdRef.current === myRunId;
    const apply = (next: Partial<GeoDetectResult>) => {
      if (!isLatest()) return;
      setResult((prev) => ({ ...prev, ...next }));
    };

    apply({ status: 'requesting-permission', isWorking: true, errorMessage: null });

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      writePermissionState('unsupported');
      apply({
        status: 'unsupported',
        isWorking: false,
        errorMessage: 'Geolocation is not supported in this browser.',
      });
      return;
    }

    // http:// on a non-localhost host never shows a permission popup.
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      writePermissionState('denied');
      apply({
        status: 'permission-denied',
        isWorking: false,
        errorMessage: BROWSER_GEO_BLOCKED_MESSAGE,
      });
      return;
    }

    // IMPORTANT: call getCurrentPosition synchronously from the user-gesture
    // turn. Awaiting anything (e.g. permissions.query) first can drop user
    // activation in Chrome/Safari so the native Allow/Block dialog never
    // appears. If the origin was already blocked, this rejects immediately
    // with PERMISSION_DENIED and no dialog — we surface enable guidance then.
    const position = await requestCurrentPosition().catch((err: GeolocationPositionError) => {
      const isDenial = err.code === err.PERMISSION_DENIED;
      // Only persist hard deny. Timeouts / unavailable must NOT set
      // `dismissed` — that was suppressing the site-wide soft prompt for
      // the rest of the session even though the user never said no.
      if (isDenial) {
        writePermissionState('denied');
      }
      apply({
        status: 'permission-denied',
        isWorking: false,
        errorMessage: isDenial
          ? BROWSER_GEO_BLOCKED_MESSAGE
          : 'Could not determine your location. Please try again or select manually.',
      });
      return null;
    });

    if (!position) return;
    writePermissionState('granted');

    const { latitude: lat, longitude: lng } = position.coords;
    apply({ status: 'geocoding', lat, lng });

    let reverse;
    try {
      reverse = await reverseGeocode(lat, lng);
    } catch (err) {
      apply({
        status: 'error',
        isWorking: false,
        lat,
        lng,
        errorMessage:
          err instanceof Error
            ? `Couldn't look up your location: ${err.message}`
            : 'Unknown error during location lookup.',
      });
      return;
    }

    if (!reverse.stateName && !reverse.districtName) {
      applyNoGeoMatch(apply, reverse, lat, lng);
      return;
    }

    apply({
      status: 'matching',
      rawStateName: reverse.stateName,
      rawDistrictName: reverse.districtName,
      pincode: reverse.pincode,
    });

    // Fetch the WCL state list directly — we own the matching here rather
    // than asking the parent to pass it down, so this hook is drop-in.
    // The wcl-api.service has its own in-memory + IndexedDB cache layer,
    // so calling it again from this hook isn't a duplicate Strapi hit.
    let states: NamedItem[] = [];
    try {
      states = await wclApiService.getStates();
    } catch {
      apply({
        status: 'error',
        isWorking: false,
        errorMessage: 'Could not load state list for matching.',
      });
      return;
    }

    const matchedState = reverse.stateName ? matchState(reverse.stateName, states) : null;
    if (!matchedState) {
      apply({
        status: 'no-match',
        isWorking: false,
        errorMessage: `We don't have dealers in ${reverse.stateName ?? 'your area'} yet. Please pick a different state.`,
      });
      writeGeoCache(emptyMatchCacheEntry(lat, lng, reverse));
      return;
    }

    // Fetch districts for the matched state and try matching the
    // district name. District match is best-effort — if it fails, we
    // still surface the state match so the user only has to pick the
    // district manually instead of both.
    let districts: NamedItem[] = [];
    try {
      districts = await wclApiService.getDistricts('', matchedState.id);
    } catch {
      // District fetch failed but state is good — surface partial match.
      applyPartialStateMatch(apply, {
        matchedState,
        reverse,
        lat,
        lng,
        errorMessage:
          'Detected your state but could not load districts. Please select district manually.',
        rawDistrictName: reverse.districtName,
      });
      return;
    }

    const resolvedDistrict = resolveDistrictMatch(reverse, districts);

    if (!resolvedDistrict) {
      applyPartialStateMatch(apply, {
        matchedState,
        reverse,
        lat,
        lng,
        errorMessage: `Detected ${matchedState.name}, but your specific district isn't in our network. Please pick from the list.`,
        rawDistrictName: reverse.districtName ?? reverse.city ?? null,
      });
      return;
    }

    // Full success.
    applyFullMatch(apply, { matchedState, resolvedDistrict, reverse, lat, lng });
  }, []);

  // Auto-run on mount (cache → permission → geocode → match).
  useEffect(() => {
    if (!enabled || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;

    // 1. Check cache first — instant win, no permission prompt.
    setResult((prev) => ({ ...prev, status: 'loading-cache' }));
    const cached = readGeoCache();
    if (cached && cached.stateId) {
      setResult(cacheHitResult(cached));
      return;
    }

    // Also treat a pincode-only cache as a hit for form autofill callers.
    if (cached && cached.pincode) {
      setResult(cacheHitResult(cached));
      return;
    }

    if (!autoPrompt) {
      setResult((prev) => ({ ...prev, status: 'idle', isWorking: false }));
      return;
    }

    // 2. Skip auto-prompt if user already declined this session.
    const sessionPerm: PermissionState = readPermissionState();
    if (sessionPerm === 'denied' || sessionPerm === 'dismissed') {
      setResult({
        ...INITIAL,
        status: 'permission-denied',
      });
      return;
    }
    if (sessionPerm === 'unsupported') {
      setResult({ ...INITIAL, status: 'unsupported' });
      return;
    }

    // 3. Fire the full detect flow.
    void runDetect();
  }, [enabled, autoPrompt, runDetect]);

  // Another hook instance (e.g. GlobalLocationPrompt) may write the shared
  // cache while this instance is still idle — adopt it so dealer UI banners
  // and autofill stay in sync without a second GPS prompt.
  useEffect(() => {
    const adoptCache = () => {
      const cached = readGeoCache();
      if (!cached || (!cached.stateId && !cached.pincode)) return;
      setResult((prev) => reconcileAdoptedCache(prev, cached));
    };

    window.addEventListener(GEO_UPDATED_EVENT, adoptCache);
    return () => window.removeEventListener(GEO_UPDATED_EVENT, adoptCache);
  }, []);

  const triggerDetect = useCallback(() => {
    // User-initiated click bypasses the session "denied/dismissed"
    // record — they're explicitly asking again. Clear the stored
    // permission state so we retry. The native popup only appears when
    // the browser still considers permission `prompt`; if the origin was
    // hard-blocked, runDetect surfaces enable-in-browser guidance.
    writePermissionState('unknown');
    hasAutoRunRef.current = true;
    void runDetect();
  }, [runDetect]);

  return {
    ...result,
    triggerDetect,
  };
}

/** Re-exported for callers that want to introspect / clear the cache. */
export type { GeoCacheEntry };
